import React, { useEffect, useState, useRef, useMemo } from "react"
import Dropdown from "react-bootstrap/Dropdown"
import {
  Button,
  Card,
  CardBody,
  CardImg,
  Col,
  Input,
  Row,
  Spinner,
  CardTitle,
} from "reactstrap"
import { useNavigate, Link } from "react-router-dom"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import a1 from "../../assets/images/a1.png"
import a2 from "../../assets/images/a2.png"
import a3 from "../../assets/images/a3.png"

import NavTabs from "../../additional_components/NavTabs"
import Breadcrumb from "../../components/Common/Breadcrumb"
import { FaEye, FaArrowsAlt, FaTrash } from "react-icons/fa"
import { api } from "../../utils/variables"
import { buildKeywordSearchParams, KEYWORD_SEARCH_PATH, normalizeSearchGames } from "../../utils/keywordSearch"
import axios from "axios"
import GuestRestrictedPlaceholder from "../../components/Common/GuestRestrictedPlaceholder"

import AppSearchModal from "../../additional_components/common/Modals/AppSearchModal"
import KeywordTrackingLandingPageModal from "../../components/Common/Modal/KeywordTrackingLandingPageModal"
import KeywordTrackingDetailsModal from "../../components/Common/Modal/KeywordTrackingDetailsModal"
import AppsTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import { createGroup } from "./KeywordTrackingServices"
import SkeletonCard from "./components/SkeletonCard"
import MaintenanceBadge from "../../components/Common/MaintenanceBadge"
import ConfirmRemoveTagModal from "../../components/Common/Modal/ConfirmRemoveTagModal"
import { Info, ShieldAlert, AlertTriangle, PlusIcon, Gamepad2, Search } from "lucide-react";
import PlatformIcon from "../../components/Common/PlatformIcon"
import PlatformTabs from "../../components/Common/PlatformTabs"
import { usePlatforms } from "../../contexts/PlatformsContext"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import SEO from "../../components/Common/SEO"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"


const CategoryTrackingLandingPage = () => {
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const { normalizePlatformName } = usePlatforms()

  const [selectedGroup, setSelectedGroup] = useState("all")
  // Filter tracked apps/games by platform (Meta Quest / Steam / Apple Vision / etc.), default All Platforms
  const [selectedPlatform, setSelectedPlatform] = useState("all")
  // Single-select platform pill row - picking one platform replaces whatever was
  // selected before; clicking the already-active platform clears back to "all"
  // (same behavior as the home dashboard's Most Tracked Games / Top Performer
  // Apps platform pills).
  const handlePlatformToggle = (platformId) => {
    setSelectedPlatform(prev => (prev === platformId ? "all" : platformId))
  }
  const [trackedGames, setTrackedGames] = useState([])
  const [allTrackedGames, setAllTrackedGames] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [gamesInGroup, setGamesInGroup] = useState([])
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false)
  const [groupNameInput, setGroupNameInput] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false) // set true initially to see the modal
  const [showAppSearchModal, setShowAppSearchModal] = useState(false)
  const [createdGroupName, setCreatedGroupName] = useState("")
  const [hoveredButtonIndex, setHoveredButtonIndex] = useState(null)
  const [isHovered, setIsHovered] = useState(false)
  const [hoveredIndex, setHoveredIndex] = useState(null)
  // track hovered row and hovered button type
  const [hoveredRow, setHoveredRow] = useState(null) // row index
  const [hoveredButton, setHoveredButton] = useState(null)
  const [skeletonCount, setSkeletonCount] = useState(0)
  const [tagInput, setTagInput] = useState("")
  const [searchResults, setSearchResults] = useState([])
  const [showDetailsModal, setDetailsShowModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [activeGroup, setActiveGroup] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);
  const [filteredGames, setFilteredGames] = useState([]) // Add this for filtered games
  const [isAllGamesHovered, setIsAllGamesHovered] = useState(false)
  const [dragOverGroup, setDragOverGroup] = useState(null)

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showGroupDeleteModal, setShowGroupDeleteModal] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState(null)

  const [resolving, setResolving] = useState(false)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const indexOfLastGame = currentPage * itemsPerPage;
  const indexOfFirstGame = indexOfLastGame - itemsPerPage;
  const platformFilteredGames = filteredGames.filter(
    game => selectedPlatform === "all" || normalizePlatformName(game.platform) === selectedPlatform
  );
  const currentGames = platformFilteredGames.slice(indexOfFirstGame, indexOfLastGame);

  // Identify if current session is browsing as a Guest
  const isGuest = useMemo(() => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"))
      return (
        authUser?.data?.is_guest === true || authUser?.is_guest === true
      )
    } catch (_) {
      return false
    }
  }, [])


  // Upgrade error detection functions
  const isUpgradeError = (error) => {
    const response = error?.response;
    const data = response?.data || {};
    const message = data?.message || '';
    const action = data?.action || '';
    return response?.status === 400 && (action === 'UPGRADE' || /max limit/i.test(message));
  };

  const isUpgradeResponse = (data) => {
    if (!data || typeof data !== 'object') return false;
    const message = data?.message || '';
    const action = data?.action || '';
    const status = data?.status;
    return status === 400 && (action === 'UPGRADE' || /max limit/i.test(message));
  };

  // Create Tag
  const [createTag, setCreateTag] = useState(null)
  // When user clicks Track while in create-group mode, remember the target group
  // so that after the user tracks the category on the details page we can add it
  // into the newly created group automatically.
  const pendingAddToGroupRef = useRef(null)
  const draggedItemId = useRef(null)
  const dragSourceGroup = useRef(null)
  const isDraggingCardRef = useRef(false)
  const moveToastSuppress = useRef(new Map())

  // "view"|"drag"|"delete"

  // factory that knows both index & type
  const btnStyle = (rowIdx, type) => ({
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    background:
      hoveredRow === rowIdx && hoveredButton === type ? "#3A3C46" : "#2A2C38",
    border: "none",
    cursor: type === "drag" ? "grab" : "pointer",
    color: type === "delete" ? "#F6255A" : "#FFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s ease",
  })

  const [showModal, setShowModal] = useState(false)
  const [gameToMove, setGameToMove] = useState(null)

  const handleOpen = (game) => {
    setGameToMove(game);
    setShowModal(true);
  }
  const handleClose = () => {
    setShowModal(false);
    setGameToMove(null);
  }

  useEffect(() => {
    callAppTrackapi()
    getGameTags()
  }, [])

  useEffect(() => {
    fetchUserTrackedKeywords()
    // fetchGroups()
  }, [])

  // Initialize filteredGames when trackedGames changes and no group is selected
  useEffect(() => {
    if (!activeGroup && trackedGames.length > 0) {
      setFilteredGames(trackedGames)
    }
  }, [trackedGames, activeGroup])

  // Add filtering effect based on activeGroup
  useEffect(() => {
    filterGamesByGroup()
  }, [activeGroup, trackedGames, groups])

  // Reset to first page when the platform filter changes
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedPlatform])

  // Listen for keyword group updates
  useEffect(() => {
    const handleKeywordGroupsUpdated = () => {
      getGameTags();
      fetchUserTrackedKeywords();
    };

    window.addEventListener('keywordGroupsUpdated', handleKeywordGroupsUpdated);

    return () => {
      window.removeEventListener('keywordGroupsUpdated', handleKeywordGroupsUpdated);
    };
  }, []);

  // Listen for tracking events from details page (category tracked)
  useEffect(() => {
    const onKeywordTracked = async e => {
      try {
        const itemId = e?.detail?.item_id || e?.detail?.itemId || null

        // Refresh tracked categories/apps first
        await callAppTrackapi()

        // If there is a pending create-group target, use createGroupApi to create the group
        // and add the newly tracked item into it.
        if (pendingAddToGroupRef.current && itemId) {
          try {
            const gameWithGroup = {
              tag_name: pendingAddToGroupRef.current,
              item_id: itemId,
            }
            await createGroupApi(gameWithGroup)
          } catch (err) {
            console.error('Auto add to pending category group failed', err)
          }
          pendingAddToGroupRef.current = null
          setCreateTag(null)
          setDetailsShowModal(false)
        }

        await getGameTags()
        await fetchUserTrackedKeywords()
      } catch (err) {
        console.error('Refresh after category tracked failed', err)
      }
    }

    window.addEventListener('keywordTracked', onKeywordTracked)
    return () => window.removeEventListener('keywordTracked', onKeywordTracked)
  }, [])

  // Function to filter games based on active group
  const filterGamesByGroup = () => {
    if (!activeGroup) {
      // Show all games when no group is selected
      setFilteredGames(trackedGames);
      return;
    }

    // Find the selected group
    const selectedGroup = groups.find(group => group.name === activeGroup);

    if (selectedGroup && selectedGroup.game_ids && trackedGames.length > 0) {
      // Filter games that belong to this group
      const filtered = trackedGames.filter(game =>
        selectedGroup.game_ids.includes(game.item_id)
      );
      setFilteredGames(filtered);
    } else if (selectedGroup && (!selectedGroup.game_ids || selectedGroup.game_ids.length === 0)) {
      // Group exists but has no games
      setFilteredGames([]);
    } else {
      // Fallback to all games
      setFilteredGames(trackedGames);
    }
  }

  const fetchUserTrackedKeywords = async () => {
    try {
      const cachedData = localStorage.getItem("userTrackedCategory")
      if (cachedData) {
        const parsedData = JSON.parse(cachedData)
        if (parsedData && Array.isArray(parsedData)) {
          // Sort cached data DESC by tracked_on
          const sortedData = [...parsedData].sort((a, b) => new Date(b.tracked_on || 0) - new Date(a.tracked_on || 0))
          setTrackedGames(sortedData)
          // Only set filteredGames if we don't have an active group filter yet
          if (!activeGroup) {
            setFilteredGames(sortedData)
          }
          setLoading(false) // Show cached data immediately
        }
      } else {
        setLoading(true) // Only show loading if no cache
      }

      const response = await axios.get(
        `${api}/tracker/tracking/get_user_tracked_category`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            Accept: "application/json, text/plain, */*",
          },
        }
      )

      if (isUpgradeResponse(response.data)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
        return;
      }

      if (response.data.status) {
        const g_data = response.data.data.game_data

        // Sort fresh data DESC by tracked_on
        const sortedData = [...(g_data || [])].sort((a, b) => new Date(b.tracked_on || 0) - new Date(a.tracked_on || 0))

        setTrackedGames(sortedData)
        if (!activeGroup) {
          setFilteredGames(sortedData)
        }

        // Update cache with sorted data
        localStorage.setItem("userTrackedCategory", JSON.stringify(sortedData))
      }

      // setSkeletonCount(games.length) // ← set number of skeletons before data displays
    } catch (error) {
      console.error("Error fetching tracked keywords:", error)
      if (isUpgradeError(error)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
      }
    } finally {
      setLoading(false)
    }
  }
  const handleSearch = async () => {
    if (!searchTerm.trim()) return

    setLoading(true)
    trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_SEARCH, { genre: searchTerm, platform: selectedPlatform })
    try {
      const params = new URLSearchParams(buildKeywordSearchParams(searchTerm))
      const response = await axios.get(
        `${api}${KEYWORD_SEARCH_PATH}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )

      if (isUpgradeResponse(response.data)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
        setSearchResults([]);
        return;
      }

      setSearchResults(normalizeSearchGames(response.data?.games || []))
    } catch (error) {
      console.error("Search failed:", error)
      if (isUpgradeError(error)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
      }
      setSearchResults([])
    } finally {
      setLoading(false)
    }
  }

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem("authToken")

      const res = await fetch(`${api}/tracker/tags.keywords?tag_type=CATEGORY`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      })

      const data = await res.json()

      if (isUpgradeResponse(data)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
        return;
      }

      if (res.status === 200) {
        setGroups(data.data)
      } else {
        console.error("Failed with status", res.status)
      }
    } catch (err) {
      console.error("Error:", err)
      if (isUpgradeError(err)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
      }
    }
  }

  const callAppTrackapi = async () => {
    await axios
      .get(`${api}/tracker/tracking/app`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then(res => {
        if (isUpgradeResponse(res.data)) {
          setShowUpgradeModal(true);
          setDetailsShowModal(false);
          setShowCreateGroupModal(false);
          return;
        }
        setAllTrackedGames(res.data.games)

        //adding data in session item
        sessionStorage.setItem(
          "comparisonBetween",
          JSON.stringify(res.data.games)
        )
      })
      .catch(error => {
        console.error("Error fetching app data:", error)
        if (isUpgradeError(error)) {
          setShowUpgradeModal(true);
          setDetailsShowModal(false);
          setShowCreateGroupModal(false);
        }
      })
    setLoading(false)
  }

  const createGroupApi = async gameWithGroup => {
    setLoading(true)
    const payload = {
      ...gameWithGroup,
      tag_type: "CATEGORY"
    }
    await axios
      .post(`${api}/tracker/tags.keywords/`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then(res => {
        if (isUpgradeResponse(res.data)) {
          setShowUpgradeModal(true);
          setDetailsShowModal(false);
          setShowCreateGroupModal(false);
          return;
        }
        getGameTags()
        setIsModalOpen(false)
      })
      .catch(error => {
        console.error("Error creating group:", error)
        if (isUpgradeError(error)) {
          setShowUpgradeModal(true);
          setDetailsShowModal(false);
          setShowCreateGroupModal(false);
        }
      })
    setLoading(false)
  }

  const createEmptyGroup = async tagName => {
    if (!tagName) return

    // Check for duplicate group name
    const isDuplicate = groups.find(g => g.name === tagName)
    if (isDuplicate) {
      toastr.error(`Group "${tagName}" already exists`)
      return
    }

    setGroups(prev => {
      if (prev.find(g => g.name === tagName)) return prev
      const next = [...prev, { name: tagName, game_ids: [] }]
      try {
        localStorage.setItem("categoryGroups", JSON.stringify(next))
      } catch (e) { }
      return next
    })

    try {
      const payload = { tag_name: tagName, tag_type: "CATEGORY" }
      const res = await axios.post(`${api}/tracker/tags.keywords/`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      })
      if (isUpgradeResponse(res.data)) {
        setShowUpgradeModal(true)
        setDetailsShowModal(false)
        setShowCreateGroupModal(false)
        return
      }
      await getGameTags()
      triggerToast(`Group "${tagName}" created successfully`)
    } catch (err) {
      console.error("Error creating empty group:", err)
      if (isUpgradeError(err)) {
        setShowUpgradeModal(true)
        setDetailsShowModal(false)
        setShowCreateGroupModal(false)
      } else {
        toastr.error(`Failed to create group "${tagName}"`)
      }
    }
  }

  const getGameTags = () => {
    axios
      .get(`${api}/tracker/tags.keywords?tag_type=CATEGORY`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then(res => {
        if (isUpgradeResponse(res.data)) {
          setShowUpgradeModal(true);
          setDetailsShowModal(false);
          setShowCreateGroupModal(false);
          return;
        }
        setGroups(res.data.data)
      })
      .catch(error => {
        console.error("Error fetching game tags:", error)
        if (isUpgradeError(error)) {
          setShowUpgradeModal(true);
          setDetailsShowModal(false);
          setShowCreateGroupModal(false);
        }
      })
  }

  const triggerToast = msg => {
    try {
      toastr.success(msg)
    } catch (err) {
      console.log("Toast:", msg)
    }
  }

  const createNewGameTag = async (itemId, tagName, suppressToast = false) => {
    if (!itemId || !tagName || tagName === "all") return
    try {
      try {
        await ensureAppTracked(itemId)
      } catch (err) {
        console.error("Could not ensure item tracked before tagging", err)
        return
      }

      const payload = {
        tag_name: tagName,
        item_id: itemId,
        tag_type: "CATEGORY",
      }

      setGroups(prev => {
        const found = prev.find(g => g.name === tagName)
        if (found) {
          if (!found.game_ids) found.game_ids = []
          if (!found.game_ids.map(String).includes(String(itemId))) {
            return prev.map(g =>
              g.name === tagName ? { ...g, game_ids: [...g.game_ids, itemId] } : g
            )
          }
          return prev
        } else {
          return [...prev, { name: tagName, game_ids: [itemId] }]
        }
      })

      const res = await axios.post(`${api}/tracker/tags.keywords/`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })

      if (isUpgradeResponse(res.data)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
        return
      }

      await getGameTags()

      let suppressedByMove = false
      try {
        const m = moveToastSuppress.current
        if (m && m.get && m.get(String(itemId)) === tagName) {
          suppressedByMove = true
          m.delete(String(itemId))
        }
      } catch (e) { }

      if (!suppressToast && !suppressedByMove) triggerToast(`Added to ${tagName}`)
    } catch (error) {
      console.error("Error adding item to group", error)
      if (isUpgradeError(error)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
      } else {
        toastr.error(`Failed to add to ${tagName}`)
      }
    }
  }

  const deleteGamefromTag = async (itemId, tagName) => {
    if (!itemId || !tagName) return
    try {
      setGroups(prev =>
        prev.map(g =>
          g.name === tagName
            ? { ...g, game_ids: (g.game_ids || []).filter(id => String(id) !== String(itemId)) }
            : g
        )
      )

      const payload = {
        item_id: itemId,
        tag_name: tagName,
        tag_type: "CATEGORY",
      }

      const res = await axios.patch(
        `${api}/tracker/tags.keywords/${encodeURIComponent(tagName)}/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )

      if (isUpgradeResponse(res.data)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
        return
      }

      await getGameTags()
    } catch (err) {
      console.error("Error removing item from group", err)
      if (isUpgradeError(err)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
      }
    }
  }

  const handleAddSingleToTag = async (itemId, tagName) => {
    if (!itemId || !tagName) return
    try {
      await createNewGameTag(itemId, tagName)
      await getGameTags()
      await callAppTrackapi()
      setGamesInGroup([])
    } catch (error) {
      console.error("handleAddSingleToTag failed:", error)
      if (isUpgradeError(error)) setShowUpgradeModal(true)
    }
  }

  const handleBulkAddToTag = async (itemIds = [], tagName) => {
    if (!Array.isArray(itemIds) || !tagName) return
    try {
      await Promise.all(itemIds.map(id => createNewGameTag(id, tagName)))
      await getGameTags()
      await callAppTrackapi()
      setGamesInGroup([])
    } catch (error) {
      console.error("Bulk add failed:", error)
      if (isUpgradeError(error)) setShowUpgradeModal(true)
    }
  }

  const handleModalRemoveFromTag = async (itemId, tagName = null) => {
    try {
      if (tagName) {
        await deleteGamefromTag(itemId, tagName)
        await getGameTags()
      } else {
        await axios.post(
          `${api}/tracker/tracking/remove_entity`,
          {
            game_id: itemId,
            entity_type: "CATEGORY",
            entity_id: null,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
          }
        )
        await fetchUserTrackedKeywords()
      }
    } catch (error) {
      console.error("remove from modal failed:", error)
      if (isUpgradeError(error)) setShowUpgradeModal(true)
    }
  }

  const toTitleCase = str =>
    str.replace(
      /\w\S*/g,
      txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
    )

  const alreadySelectedGames = (trackedGames || []).map(game => ({
    name: game.game_details?.name || game.name || "",
    image_url: game.game_details?.image_url || game.image_url || "",
    item_id: game.game_details?.item_id || game.item_id || "",
    platform: game.game_details?.platform || game.platform || "",
  }))

  const trackedIds = useMemo(
    () =>
      (trackedGames || []).map(g => String(g.game_details?.item_id || g.item_id || "")),
    [trackedGames]
  )

  const ensureAppTracked = async item => {
    const trackedAppIds = (allTrackedGames || []).map(g =>
      String(g.game_details?.item_id || g.item_id || "")
    )
    const idStr = String(item.item_id || item.game_details?.item_id || item)
    if (trackedAppIds.includes(idStr)) return
    try {
      const res = await axios.post(
        `${api}/tracker/tracking/app/`,
        { item_id: item.item_id || item },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        }
      )
      if (isUpgradeResponse(res.data)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
        return
      }
      await callAppTrackapi()
    } catch (err) {
      console.error("Error tracking app:", err)
      if (isUpgradeError(err)) setShowUpgradeModal(true)
      throw err
    }
  }

  const handleselectedapp = async app => {
    try {
      if (pendingAddToGroupRef.current) {
        const target = pendingAddToGroupRef.current
        const appId = app.item_id || app.itemId || app
        if (trackedIds.includes(String(appId))) {
          try {
            await createNewGameTag(appId, target)
            await getGameTags()
            await callAppTrackapi()
          } catch (err) {
            console.error("Auto add to pending group failed", err)
            if (isUpgradeError(err)) setShowUpgradeModal(true)
          }
          pendingAddToGroupRef.current = null
          setDetailsShowModal(false)
          return
        }

        setDetailsShowModal(false)
        // Clear local storage for this app before navigating to ensure clean state
        try {
          const appId = app.item_id || app.itemId || app
          const prefix = `category_tracking_${appId}_`
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith(prefix)) {
              localStorage.removeItem(key)
            }
          })
        } catch (e) {
          console.error("Error clearing cleanup storage:", e)
        }
        navigate(`/xr/category/${(app.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${app.item_id}`, { state: { item_id: app.item_id } })
        return
      }

      if (createTag) {
        pendingAddToGroupRef.current = createTag
        setDetailsShowModal(false)
        // Clear local storage for this app before navigating to ensure clean state
        try {
          const appId = app.item_id || app.itemId || app
          const prefix = `category_tracking_${appId}_`
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith(prefix)) {
              localStorage.removeItem(key)
            }
          })
        } catch (e) {
          console.error("Error clearing cleanup storage:", e)
        }
        navigate(`/xr/category/${(app.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${app.item_id}`, { state: { item_id: app.item_id } })
      } else {
        setDetailsShowModal(false)
        // Clear local storage for this app before navigating to ensure clean state
        try {
          const appId = app.item_id || app.itemId || app
          const prefix = `category_tracking_${appId}_`
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith(prefix)) {
              localStorage.removeItem(key)
            }
          })
        } catch (e) {
          console.error("Error clearing cleanup storage:", e)
        }
        navigate(`/xr/category/${(app.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${app.item_id}`, { state: { item_id: app.item_id } })
      }
    } catch (err) {
      console.error("handleselectedapp failed:", err)
      if (isUpgradeError(err)) setShowUpgradeModal(true)
    }
  }

  const handleDragStart = (e, itemId, sourceGroupName = null) => {
    draggedItemId.current = itemId
    dragSourceGroup.current = sourceGroupName
  }

  const handleDropOnGroup = async (e, targetGroupName) => {
    if (e?.preventDefault) e.preventDefault()
    const itemId = draggedItemId.current
    if (!itemId || !targetGroupName) return
    try {
      if (dragSourceGroup.current === targetGroupName) return

      const targetGroup = groups.find(group => group.name === targetGroupName)
      const alreadyInTarget = targetGroup?.game_ids
        ?.map(String)
        .includes(String(itemId))
      if (alreadyInTarget) {
        triggerToast(`App already in ${targetGroupName}`)
        return
      }

      const addPromise = createNewGameTag(itemId, targetGroupName, true)
      setActiveGroup(targetGroupName)
      await addPromise

      triggerToast(`Added app to ${targetGroupName}`)
    } catch (err) {
      console.error("drag drop to group failed:", err)
      if (isUpgradeError(err)) setShowUpgradeModal(true)
    } finally {
      draggedItemId.current = null
      dragSourceGroup.current = null
      setDragOverGroup(null)
      isDraggingCardRef.current = false
    }
  }

  const handleGroupDragEnter = (e, groupName) => {
    e.preventDefault()
    setDragOverGroup(groupName)
  }

  const handleGroupDragOver = (e, groupName) => {
    e.preventDefault()
    if (dragOverGroup !== groupName) {
      setDragOverGroup(groupName)
    }
  }

  const handleGroupDragLeave = e => {
    e.preventDefault()
    setDragOverGroup(null)
  }

  const handleGroupDropZone = (e, groupName) => {
    e.preventDefault()
    setDragOverGroup(null)
    handleDropOnGroup(e, groupName)
  }

  const handleCardDragStart = (e, game) => {
    if (!game?.item_id) return
    isDraggingCardRef.current = true
    try {
      e.dataTransfer.effectAllowed = "copy"
      e.dataTransfer.setData("text/plain", String(game.item_id))
      const imgEl =
        e.currentTarget?.querySelector(".char_inner_box_lt_img img") || null
      if (imgEl) {
        const rect = imgEl.getBoundingClientRect()
        e.dataTransfer.setDragImage(imgEl, rect.width / 2, rect.height / 2)
      }
    } catch (err) { }
    handleDragStart(e, game.item_id, activeGroup || null)
  }

  const handleCardDragEnd = () => {
    isDraggingCardRef.current = false
    draggedItemId.current = null
    dragSourceGroup.current = null
    setDragOverGroup(null)
  }

  const handleCardClick = (e, game) => {

    if (e.target.closest("[data-stop-card-click]")) {
      return
    }

    if (isDraggingCardRef.current) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    navigate(`/xr/category/${(game.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${game.item_id}`, { state: { item_id: game.item_id } })
  }

  const groupButtonStyle = (label, isHovered) => {
    const labelText = typeof label === "string" ? label : label.name
    const screenWidth = window.innerWidth
    const widthBase = screenWidth > 1400 ? 140 : screenWidth > 1024 ? 130 : 120
    const heightBase = screenWidth > 1400 ? 48 : screenWidth > 1024 ? 44 : 40

    const isSelected =
      selectedGroup === label || selectedGroup?.name === labelText

    const baseColor = isSelected ? "#F6255A" : "#171821"
    const hoverColor = isSelected ? "#e11d4c" : "#2A2C38" // lighter on hover

    return {
      width:
        labelText === "+ Create Groups"
          ? `${widthBase + 10}px`
          : labelText.length > 12
            ? `${widthBase + 20}px`
            : `${widthBase}px`,
      height: `${heightBase}px`,
      borderRadius: "12px",
      padding: "10px 16px",
      background: isHovered ? hoverColor : baseColor,
      color: "#FFFFFF",
      fontFamily: "Rubik, sans-serif",
      fontWeight: "500",
      fontSize: "14px",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: labelText === "+ Create Groups" ? "8px" : "0px",
      transition: "all 0.2s ease-in-out",
    }
  }
  const addButtonStyle = {
    width: "84px",
    padding: "8px 16px",
    borderRadius: "6px",
    background: isHovered ? "#e11d4c" : "#F6255A", // lighter on hover
    color: "#FFFFFF",
    border: "none",
    fontWeight: "500",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background 0.2s ease-in-out",
  }
  // Custom skeleton card
  const handleRemoveGame = async (game) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${api}/tracker/tracking/remove_entity`,
        {
          game_id: game.item_id, // Make sure this is correct
          entity_type: "CATEGORY",
          entity_id: null
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (isUpgradeResponse(response.data)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
        return;
      }

      // Clear local storage for this app to prevent zombie data resurrection
      try {
        const prefix = `category_tracking_${game.item_id}_`
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(prefix)) {
            localStorage.removeItem(key)
          }
        })
      } catch (e) {
        console.error("Error clearing cleanup storage:", e)
      }

      await fetchUserTrackedKeywords();
    } catch (error) {
      if (isUpgradeError(error)) {
        setShowUpgradeModal(true);
        setDetailsShowModal(false);
        setShowCreateGroupModal(false);
      } else {
        toastr.error("Failed to remove tracked game");
      }
      console.error("Remove failed:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleRemoveGroup = async groupName => {
    try {
      const response = await fetch(
        `${api}/tracker/tags.keywords/${groupName}/?tag_type=CATEGORY`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error(`Failed to delete group: ${response.statusText}`)
      }

      // Update UI only if API call succeeded
      setGroups(prevGroups => prevGroups.filter(g => g.name !== groupName))

      if (activeGroup === groupName) {
        setActiveGroup(null)
        setFilteredGames(trackedGames) // Reset if active group was deleted
      }

      triggerToast(`Group "${groupName}" deleted successfully`)
    } catch (error) {
      console.error("Error deleting group:", error)
      toastr.error(`Failed to delete group "${groupName}"`)
    }
  }

  const averageChange = (game) => {
    const list = game.tracked_keywords || [];
    if (list.length === 0) return 0;

    const sum = list.reduce((acc, k) => acc + (k.rank_change || 0), 0);
    return Math.round(sum / list.length);
  };


  return (
    <React.Fragment>
      <SEO title="Genre Tracking Landing" description="Manage your category tracking campaigns." />
      <div className="page-content m-3 dashboard-responsive">
        {/* Search Bar */}
        <div className=".all_section_track_innersec">
          <div className="row">
            <div className="col-sm-12">
              <h3 className="mb-3 d-flex align-items-center justify-content-between">
                <span
                  style={{
                    fontFamily: "Rubik",
                    fontWeight: 600,
                    fontSize: "32px",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  Genre Tracking
                  {/* <MaintenanceBadge
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginLeft: 8,
                    padding: "4px 8px",
                    fontSize: 12,
                    borderRadius: 6,
                    background: "rgba(255,165,0,0.95)",
                    color: "#111",
                    boxShadow: "0 1px 6px rgba(0,0,0,0.12)",
                    zIndex: 2,
                  }}
                >
                  Under Maintenance
                </MaintenanceBadge> */}
                </span>
              </h3>
              <div className="keyword_subhead">
                Track genres for your games and apps to analyze their performance
                across revenue, downloads, and other key metrics. Understand how
                your app ranks for specific genres across countries and optimize
                your presence in the Meta, Steam, and Apple Vision stores.
              </div>
            </div>
          </div>

          <div className="key_track_sec" style={{ height: "auto", minHeight: 0 }}>
            {/* Common Left Margin Container */}

            {/* <h4> Tracked Apps and genres</h4> */}
            <div
              className="d-flex align-items-center justify-content-between flex-wrap gap-2"
              style={{ marginBottom: "8px" }}
            >
              <h4 style={{ margin: 0 }}>Tracked Apps & Genres</h4>

              <button
                onClick={() => navigate("/app-tracking")}
                disabled={resolving}
                className="go-to-publisher-btn"
                aria-label="Go to App Tracking page"
              >
                {resolving ? (
                  <span className="go-loading">Opening...</span>
                ) : (
                  <>
                    <svg
                      className="go-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 2L12 12M5 9L12 16L19 9"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="go-label">Looking for App Tracking?</span>
                  </>
                )}
              </button>
            </div>

            <p
              className="note-text"
              style={{
                fontStyle: "italic",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Info size={16} color="#A1ACB8" />
              Track an app first, and then track its related genres for deeper
              insights.
            </p>

            <div className="key_track_search d-flex align-items-center flex-row gap-3 gap-sm-4">
              <div className="key_track_search_inn searchField d-flex align-items-center">
                <i className="fa-solid fa-magnifying-glass"></i>
                <input
                  type="text"
                  placeholder="Enter App or Game Name or URL"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleSearch()
                      setDetailsShowModal(true)
                    }
                  }}
                />
              </div>
              <button
                className="searchBtn"
                onClick={() => {
                  handleSearch() // ✅ Call the API
                  setDetailsShowModal(true) // ✅ Then show modal
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={addButtonStyle}
              >
                Search
              </button>
            </div>

            <div
              className="custom_grp_sec"
              style={{
                // maxHeight: "calc(100vh - 100px)", // adjust based on header/footer
                overflowY: "auto",
                paddingBottom: "20px",
              }}
            >
              <p
                className="disclaimer-text"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontStyle: "italic",
                }}
              >
                <Info size={16} color="#A1ACB8" />
                These are your tracked apps and games. Click on any app below to
                go directly to its genre tracking page.
              </p>

              {/* FIRST ROW — ALL GAMES */}
              <ul
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 14,
                }}
              >
                <li>
                  <button
                    onClick={() => {
                      setActiveGroup(null)
                      setFilteredGames(trackedGames)
                    }}
                    className={!activeGroup ? "active" : ""}
                    onMouseEnter={() => setIsAllGamesHovered(true)}
                    onMouseLeave={() => setIsAllGamesHovered(false)}
                    style={{
                      width: "auto",
                      padding: "8px 12px",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 700,
                      background: !activeGroup
                        ? isAllGamesHovered
                          ? "#e11d4c"
                          : "#F6255A"
                        : "#2A2B36",
                      color: !activeGroup ? "#FFFFFF" : "#A1ACB8",
                      border: "none",
                      boxShadow: !activeGroup
                        ? isAllGamesHovered
                          ? "0 8px 24px rgba(246,37,90,0.16)"
                          : "0 6px 20px rgba(246,37,90,0.12)"
                        : "none",
                      transition: "background 0.18s ease, transform 0.12s ease",
                    }}
                    aria-label="Show all apps and games"
                  >
                    <i
                      className="fa-solid fa-th-large"
                      style={{ marginRight: 8, fontSize: 12 }}
                      aria-hidden="true"
                    ></i>
                    All Apps and Games
                  </button>
                </li>
              </ul>

              <h6
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Custom Groups</span>
              </h6>
              <ul
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  marginTop: 10,
                }}
              >
                {groups.map((group, idx) => {
                  const isDragTarget = dragOverGroup === group.name
                  return (
                    <li
                      key={idx}
                      onDragEnter={e => handleGroupDragEnter(e, group.name)}
                      onDragOver={e => handleGroupDragOver(e, group.name)}
                      onDragLeave={handleGroupDragLeave}
                      onDrop={e => handleGroupDropZone(e, group.name)}
                    >
                      <a
                        className={activeGroup === group.name ? "active" : ""}
                        onClick={() => setActiveGroup(group.name)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 10px",
                          cursor: "pointer",
                          textDecoration: "none",
                          borderRadius: 8,
                          transition:
                            "background 0.12s ease, transform 0.08s ease, box-shadow 0.12s ease",

                          // ⭐ EXACT same background behavior as the FIRST BLOCK
                          // dragging → #F6255A background, white text
                          background: isDragTarget ? "#F6255A" : undefined,
                          color: isDragTarget ? "#ffffff" : undefined,

                          // ⭐ NEW: ONLY show dashed border on drag (from second block)
                          border: isDragTarget
                            ? "1px dashed #ffffff"
                            : "1px solid transparent",

                          // ❗ REMOVE lift and glow from first block
                          boxShadow: "none",
                          transform: "none",
                        }}
                      >
                        <span>{group.name}</span>
                        <span
                          onClick={e => {
                            e.stopPropagation()
                            setGroupToDelete(group.name)
                            setShowGroupDeleteModal(true)
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 640 640"
                            width="12"
                            height="12"
                            fill="currentColor"
                          >
                            <path
                              d="M183.1 137.4C170.6 124.9 150.3 124.9 137.8 137.4C125.3 149.9 125.3 170.2 137.8 182.7L275.2 320L137.9 457.4C125.4 469.9 125.4 490.2 137.9 502.7C150.4 515.2 170.7 515.2 183.2 502.7L320.5 365.3L457.9 502.6C470.4 515.1 490.7 515.1 503.2 502.6C515.7 490.1 515.7 469.8 503.2 457.3L365.8 320L503.1 182.6C515.6 170.1 515.6 149.8 503.1 137.3C490.6 124.8 470.3 124.8 457.8 137.3L320.5 274.7L183.1 137.4z" />
                          </svg>
                        </span>
                      </a>
                    </li>
                  )
                })}
                <li>
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="secondary"
                      className="createGroupBtn"
                      id="createGroup-abcz082458wer"
                    >
                      {/* <i className="fa-solid fa-plus"></i> */}
                      <i className="fas fa-plus" style={{ marginRight: 8, fontSize: 12 }} aria-hidden="true"></i>
                      {/* <PlusIcon /> */}
                      Create Group
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="p-0 border-0 m-0">
                      <div className=" create_grp_popup">
                        <h6>Create Group</h6>
                        <input
                          value={createTag}
                          onChange={e => setCreateTag(e.target.value)}
                          type="text"
                          placeholder="Enter Group Name"
                          style={{
                            background: "rgba(255, 255, 255, 0.05)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderRadius: "8px",
                            color: "#fff",
                            padding: "10px 12px",
                            width: "100%",
                            marginBottom: "16px",
                            fontSize: "14px",
                          }}
                        />
                        <div className="create_grp_popup_btn">
                          <button
                            onClick={async () => {
                              if (!createTag) return
                              document
                                .querySelector("#createGroup-abcz082458wer")
                                ?.click()

                              const newGroup = createTag
                              setCreateTag("")

                              try {
                                await createEmptyGroup(newGroup)
                              } catch (e) { }
                            }}
                            type="button"
                            className="active"
                          >
                            Create
                          </button>
                          <button
                            onClick={() => {
                              setCreateTag(null)
                              document
                                .getElementById("createGroup-abcz082458wer")
                                .click()
                            }}
                            type="button"
                            value="cancel"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </li>
              </ul>

              {/* Platform filter as pill tabs, matching Most Tracked Games /
                  Top Performer Apps on the home dashboard - single-select,
                  clicking the already-active platform clears back to "all". */}
              <div className="platform-filter-row">
                <PlatformTabs
                  variant="pill"
                  showDivider={false}
                  selected={selectedPlatform === "all" ? [] : [selectedPlatform]}
                  onToggle={handlePlatformToggle}
                />
              </div>

              <div className="select_grp_popup">
                <KeywordTrackingLandingPageModal
                  show={showModal}
                  handleClose={handleClose}
                  title="My Modal"
                  itemId={gameToMove?.item_id}
                  tagType="CATEGORY"
                >
                  <p>This is the modal content.</p>
                </KeywordTrackingLandingPageModal>
              </div>

              <div className="row">
                {activeGroup && (
                  <div className="col-12 text-end mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        pendingAddToGroupRef.current = activeGroup
                        setCreateTag(activeGroup)
                        setDetailsShowModal(true)
                      }}
                      style={{
                        background: "#F6255A",
                        color: "#fff",
                        border: "none",
                        padding: "10px 18px",
                        borderRadius: 8,
                        cursor: "pointer",
                      }}
                    >
                      Add to {activeGroup}
                    </button>
                  </div>
                )}
                {loading ? (
                  [...Array(skeletonCount || 3)].map((_, idx) => (
                    <div
                      key={idx}
                      className="col-sm-12 col-lg-12 col-xxl-12 col-md-6"
                    >
                      <SkeletonCard />
                    </div>
                  ))
                ) : isGuest ? (
                  <div className="col-12">
                    <GuestRestrictedPlaceholder 
                      height="400px"
                      subMessage="Sign in to track categories and organize your favorite VR apps into custom groups."
                    />
                  </div>
                ) : currentGames.length === 0 ? (
                  <div className="col-12">
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.03)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.05)",
                        borderRadius: "24px",
                        padding: "80px 40px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        marginTop: "20px",
                        marginBottom: "40px",
                        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          background: "linear-gradient(135deg, rgba(246, 37, 90, 0.15) 0%, rgba(246, 37, 90, 0.05) 100%)",
                          borderRadius: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "24px",
                        }}
                      >
                        <Gamepad2 size={40} color="#F6255A" />
                      </div>
                      <h2
                        style={{
                          color: "#fff",
                          fontSize: "28px",
                          fontWeight: "700",
                          marginBottom: "12px",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {activeGroup ? `No apps in this group yet` : "No Tracked Apps Yet"}
                      </h2>
                      <p
                        style={{
                          color: "rgba(255, 255, 255, 0.5)",
                          fontSize: "16px",
                          lineHeight: "1.6",
                          maxWidth: "400px",
                          marginBottom: "32px",
                        }}
                      >
                        {activeGroup
                          ? `Start organizing your genres by adding apps to the "${activeGroup}" group.`
                          : "Track and organize your favorite VR apps and genres to get real-time insights and analytics."}
                      </p>
                      <button
                        onClick={() => {
                          setDetailsShowModal(true);
                        }}
                        style={{
                          background: "#F6255A",
                          color: "#fff",
                          borderRadius: "12px",
                          padding: "12px 24px",
                          fontSize: "16px",
                          fontWeight: "600",
                          border: "none",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          transition: "all 0.2s ease",
                          boxShadow: "0 8px 16px rgba(246, 37, 90, 0.25)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 12px 20px rgba(246, 37, 90, 0.35)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 8px 16px rgba(246, 37, 90, 0.25)";
                        }}
                      >
                        <Search size={18} />
                      {activeGroup ? `Add to ${activeGroup}` : "Track Your First App"}
                    </button>
                  </div>
                </div>
              ) : (
                  currentGames.map((game, idx) => (
                    <div
                      key={idx}
                      className="col-sm-12 col-lg-12 col-xxl-12 col-md-6"
                    >
                      <div
                        className="chart_inner_sec"
                        draggable
                        onDragStart={e => handleCardDragStart(e, game)}
                        onDragEnd={handleCardDragEnd}
                        onClick={e => handleCardClick(e, game)}
                        style={{ cursor: "grab" }}
                      >
                        <div
                          className="char_inner_box"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 16,
                            width: "100%",
                          }}
                        >
                          <div
                            className="char_inner_box_lt"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              minWidth: 0,
                              flex: "1 1 auto",
                            }}
                          >
                            <div className="char_inner_box_lt_img">
                              {" "}
                              <Link
                                to={`/xr/category/${(game.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${game.item_id}`}
                                state={{ item_id: game.item_id }}
                              >
                                <img src={game.image_url} alt={game.name} />
                              </Link>
                            </div>
                            <div
                              className="char_inner_box_lt_text"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                minWidth: 0,
                                flex: "1 1 auto",
                              }}
                            >
                              <div
                                style={{
                                  minWidth: 0,
                                  fontSize: "clamp(14px, 2.2vw, 18px)",
                                  fontWeight: 600,
                                  color: "#fff",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "min(60vw, 220px)",
                                }}
                                title={game.name}
                                aria-label={game.name}
                              >
                                {game.name}
                              </div>
                              <p style={{ margin: 0, color: "#A1ACB8" }}>
                                <PlatformIcon platform={game.platform} size={16} />
                              </p>
                            </div>
                            <div
                              className="d-flex align-items-center gap-5"
                              style={{
                                background: "#1E1F29",
                                padding: "12px 18px",
                                borderRadius: "10px",
                                display: "inline-flex", // shrink to fit content
                                position: "relative",
                                flex: "0 0 auto",
                                whiteSpace: "nowrap",
                                gap: 20,
                              }}
                            >
                              {/* Tracked On */}
                              <div style={{ textAlign: "center" }}>
                                <div
                                  style={{ color: "#A1ACB8", fontSize: "12px" }}
                                >
                                  Tracked On
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#fff",
                                  }}
                                >
                                  {game.tracked_on
                                    ? new Date(
                                      game.tracked_on
                                    ).toLocaleDateString()
                                    : "--"}
                                </div>
                              </div>

                              {/* Keywords Count */}
                              <div style={{ textAlign: "center" }}>
                                <div
                                  style={{ color: "#A1ACB8", fontSize: "12px" }}
                                >
                                  Genres
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#fff",
                                  }}
                                >
                                  {game.tracking_count || 0}
                                </div>
                              </div>

                              {/* Average Change */}
                              <div style={{ textAlign: "center" }}>
                                <div
                                  style={{ color: "#A1ACB8", fontSize: "12px" }}
                                >
                                  Avg Change
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color:
                                      averageChange(game) > 0
                                        ? "#4ade80"
                                        : averageChange(game) < 0
                                          ? "#f87171"
                                          : "#fff",
                                  }}
                                >
                                  {averageChange(game)}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            className="char_inner_box_rt"
                            data-stop-card-click
                            style={{ flex: "0 0 auto", marginLeft: "auto" }}
                          >
                            <ul>
                              <li>
                                <Link
                                  className="active"
                                  to={`/xr/category/${(game.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${game.item_id}`}
                                  state={{ item_id: game.item_id }}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M21.75 12L22.3725 11.688V11.685L22.368 11.6805L22.359 11.6625L22.3275 11.6025L22.2075 11.3865C22.061 11.1339 21.9054 10.8868 21.741 10.6455C21.1921 9.83995 20.5613 9.09345 19.8585 8.41802C18.1695 6.79802 15.57 5.14502 12 5.14502C8.43295 5.14502 5.83195 6.79652 4.14295 8.41802C3.44015 9.09345 2.80927 9.83995 2.26045 10.6455C2.03776 10.9741 1.83151 11.3135 1.64245 11.6625L1.63345 11.6805L1.63045 11.685V11.6865C1.63045 11.6865 1.62895 11.688 2.25145 12L1.62895 11.6865C1.58082 11.7838 1.55579 11.8908 1.55579 11.9993C1.55579 12.1078 1.58082 12.2148 1.62895 12.312L1.62745 12.315L1.63195 12.3195L1.64095 12.3375C1.68773 12.4312 1.73776 12.5233 1.79095 12.6135C2.43685 13.7046 3.22697 14.7036 4.13995 15.5835C5.83045 17.2035 8.42995 18.8535 12 18.8535C15.5685 18.8535 18.1695 17.2035 19.86 15.582C20.5615 14.9058 21.1918 14.1594 21.741 13.3545C21.9513 13.0448 22.147 12.7255 22.3275 12.3975L22.359 12.3375L22.368 12.3195L22.371 12.315V12.3135C22.371 12.3135 22.3725 12.312 21.75 12ZM21.75 12L22.3725 12.3135C22.4206 12.2163 22.4456 12.1093 22.4456 12.0008C22.4456 11.8923 22.4206 11.7853 22.3725 11.688L21.75 12ZM11.91 9.69602C11.2989 9.69602 10.7129 9.93876 10.2808 10.3708C9.8487 10.8029 9.60595 11.389 9.60595 12C9.60595 12.6111 9.8487 13.1971 10.2808 13.6292C10.7129 14.0613 11.2989 14.304 11.91 14.304C12.521 14.304 13.107 14.0613 13.5391 13.6292C13.9712 13.1971 14.214 12.6111 14.214 12C14.214 11.389 13.9712 10.8029 13.5391 10.3708C13.107 9.93876 12.521 9.69602 11.91 9.69602ZM8.21695 12C8.21695 11.0198 8.60635 10.0797 9.29949 9.38655C9.99262 8.69342 10.9327 8.30402 11.913 8.30402C12.8932 8.30402 13.8333 8.69342 14.5264 9.38655C15.2196 10.0797 15.609 11.0198 15.609 12C15.609 12.9803 15.2196 13.9204 14.5264 14.6135C13.8333 15.3066 12.8932 15.696 11.913 15.696C10.9327 15.696 9.99262 15.3066 9.29949 14.6135C8.60635 13.9204 8.21695 12.9803 8.21695 12Z"
                                      fill="white"
                                    />
                                  </svg>
                                  <span className="hoverText">View</span>
                                </Link>
                              </li>
                              <li
                                onClick={e => {
                                  e.stopPropagation()
                                  handleOpen(game)
                                }}
                              >
                                <Link>
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <g clipPath="url(#clip0_91_3961)">
                                      <path
                                        d="M11.0016 9.99844H13.0031V5.00156H17.0016L12 0L6.99844 5.00156H10.9969V9.99844H11.0016ZM24 12L18.9984 6.99844V10.9969H14.0016V12.9984H19.0031V16.9969L24 12ZM9.99844 11.0016H5.00156V7.00312L0 12L5.00156 17.0016V13.0031H10.0031V11.0016H9.99844ZM12.9984 14.0016H10.9969V19.0031H6.99844L12 24L17.0016 18.9984H13.0031V14.0016H12.9984Z"
                                        fill="#A1ACB8"
                                      />
                                    </g>
                                    <defs>
                                      <clipPath id="clip0_91_3961">
                                        <rect
                                          width="24"
                                          height="24"
                                          fill="white"
                                        />
                                      </clipPath>
                                    </defs>
                                  </svg>
                                  <span className="hoverText">Move</span>
                                </Link>
                              </li>
                              <li
                                // onClick={() => {
                                //   setGameToDelete(game); // Set the selected game
                                //   setShowDeleteModal(true); // Open modal
                                // }}
                                onClick={e => {
                                  e.stopPropagation()
                                  setGameToDelete(game)
                                  setShowDeleteModal(true)
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <Link>
                                  {/* your SVG etc. */}
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M7 21C6.45 21 5.97933 20.8043 5.588 20.413C5.19667 20.0217 5.00067 19.5507 5 19V6H4V4H9V3H15V4H20V6H19V19C19 19.55 18.8043 20.021 18.413 20.413C18.0217 20.805 17.5507 21.0007 17 21H7ZM9 17H11V8H9V17ZM13 17H15V8H13V17Z"
                                      fill="#A1ACB8"
                                    />
                                  </svg>
                                  <span className="hoverText">Delete</span>
                                </Link>
                              </li>
                              {/* Modal moved outside loop */}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {platformFilteredGames.length > 0 && (
                <div
                  className="pagination"
                  style={{
                    marginTop: "30px",
                    marginBottom: activeGroup ? "40px" : "20px",
                    textAlign: "center",
                    display: "flex",
                    justifyContent: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {/* Previous Button */}
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      background: currentPage === 1 ? "#2A2B38" : "#F6255A",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      padding: "6px 12px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      fontWeight: 500,
                    }}
                  >
                    Prev
                  </button>

                  {/* Page Numbers */}
                  {Array.from(
                    { length: Math.ceil(platformFilteredGames.length / itemsPerPage) },
                    (_, i) => i + 1
                  ).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        background: pageNum === currentPage ? "#F6255A" : "#2A2B38",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 12px",
                        cursor: "pointer",
                        fontWeight: pageNum === currentPage ? 600 : 500,
                      }}
                    >
                      {pageNum}
                    </button>
                  ))}

                  {/* Next Button */}
                  <button
                    onClick={() =>
                      setCurrentPage(prev =>
                        Math.min(
                          prev + 1,
                          Math.ceil(platformFilteredGames.length / itemsPerPage)
                        )
                      )
                    }
                    disabled={
                      currentPage === Math.ceil(platformFilteredGames.length / itemsPerPage)
                    }
                    style={{
                      background:
                        currentPage ===
                          Math.ceil(platformFilteredGames.length / itemsPerPage)
                          ? "#2A2B38"
                          : "#F6255A",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      padding: "6px 12px",
                      cursor:
                        currentPage ===
                          Math.ceil(platformFilteredGames.length / itemsPerPage)
                          ? "not-allowed"
                          : "pointer",
                      fontWeight: 500,
                    }}
                  >
                    Next
                  </button>
                </div>
              )}

            </div>

            {showCreateGroupModal && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    background: "#21222D",
                    borderRadius: "12px",
                    padding: "24px",
                    width: "90%",
                    maxWidth: "400px",
                    color: "#FFFFFF",
                    fontFamily: "Rubik, sans-serif",
                  }}
                >
                  <h4 style={{ marginBottom: "16px", fontWeight: "600" }}>
                    Create Group
                  </h4>

                  <input
                    type="text"
                    placeholder="Enter Group Name"
                    value={groupNameInput}
                    onChange={e => setGroupNameInput(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 16px",
                      marginBottom: "24px",
                      borderRadius: "6px",
                      border: "none",
                      background: "#171821",
                      color: "#fff",
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "12px",
                    }}
                  >
                    <button
                      onClick={() => setShowCreateGroupModal(false)}
                      style={{
                        background: "#171821",
                        color: "#FFFFFF",
                        border: "none",
                        borderRadius: "6px",
                        padding: "10px 16px",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      onClick={async () => {
                        if (!groupNameInput) return
                        setCreatedGroupName(groupNameInput)
                        setShowCreateGroupModal(false)
                        pendingAddToGroupRef.current = groupNameInput
                        try {
                          await createEmptyGroup(groupNameInput)
                        } catch (e) { }
                        setGroupNameInput("")
                        setIsModalOpen(true)
                        setShowAppSearchModal(true)
                      }}
                      style={{
                        background: "#F6255A",
                        color: "#FFFFFF",
                        border: "none",
                        borderRadius: "6px",
                        padding: "10px 16px",
                        fontWeight: "500",
                        cursor: "pointer",
                      }}
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            )}
            {showGroupDeleteModal && groupToDelete && (
              <div
                style={{
                  position: "fixed",
                  left: 0,
                  top: 0,
                  width: "100vw",
                  height: "100vh",
                  background: "rgba(0,0,0,0.55)",
                  zIndex: 12345,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    background: "#21222D",
                    color: "#fff",
                    borderRadius: 16,
                    padding: 32,
                    maxWidth: 340,
                    width: "100%",
                    textAlign: "center",
                    boxShadow: "0 4px 32px #0007",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 20, marginBottom: 8 }}>
                    Delete group "{groupToDelete}"?
                  </div>
                  <div style={{ color: "#A1ACB8", marginBottom: 24 }}>
                    Are you sure you want to remove this group? This action can’t
                    be undone.
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "center", gap: 16 }}
                  >
                    <button
                      style={{
                        background: "#171821",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        padding: "10px 22px",
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                      onClick={() => setShowGroupDeleteModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      style={{
                        background: "#F6255A",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        padding: "10px 22px",
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                      onClick={async () => {
                        setShowGroupDeleteModal(false) // close modal
                        await handleRemoveGroup(groupToDelete) // call API
                        setGroupToDelete(null) // reset
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <AppSearchModal
          modaltoggle={isModalOpen}
          togglemodal={() => setIsModalOpen(!isModalOpen)}
          onAppSelect={async app => {
            try {
              setIsModalOpen(false)
              if (createdGroupName) {
                const gameWithGroup = {
                  tag_name: createdGroupName,
                  item_id: app.item_id,
                }
                await createGroupApi(gameWithGroup)
                setCreatedGroupName("")
                pendingAddToGroupRef.current = null
                return
              }

              if (pendingAddToGroupRef.current) {
                const target = pendingAddToGroupRef.current
                pendingAddToGroupRef.current = null
                try {
                  await ensureAppTracked(app.item_id)
                  await createNewGameTag(app.item_id, target)
                  trackEvent(ANALYTICS_EVENTS.FEATURE.GENRE_TRACKING_APP_ADDED, {
                    app_id: app.item_id,
                    app_name: app.name,
                    genre: target,
                  })
                  await getGameTags()
                  await callAppTrackapi()
                } catch (err) {
                  console.error("Add-to-pending-group failed", err)
                  if (isUpgradeError(err)) setShowUpgradeModal(true)
                }
                return
              }

              navigate(`/analytxrics/category/${(app.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${app.item_id}`, { state: { item_id: app.item_id } })
            } catch (err) {
              console.error("AppSelect handler failed", err)
              if (isUpgradeError(err)) setShowUpgradeModal(true)
            }
          }}
          alreadySelectedApps={alreadySelectedGames}
          type="Category"
        />
        <KeywordTrackingDetailsModal
          show={showDetailsModal}
          handleClose={() => {
            setCreateTag("")
            setDetailsShowModal(false)
          }}
          title={createTag ? "Select an App to Create Group" : "Select an App"}
          searchTerm={searchTerm}
          onSearchChange={e => setSearchTerm(e.target.value)}
          onSearchClick={handleSearch}
          results={searchResults}
          loading={loading}
          createTag={createTag}
          trackedIds={trackedIds}
          tags={groups}
          onAddToTag={handleAddSingleToTag}
          onBulkAddToTag={handleBulkAddToTag}
          onRemoveFromTag={handleModalRemoveFromTag}
          onOpenGroup={(groupName) => {
            try { setDetailsShowModal(false) } catch (e) { }
            try { setActiveGroup(groupName) } catch (e) { }
          }}
          onSelectApp={handleselectedapp}
          onUpgrade={() => {
            setShowUpgradeModal(true)
            setDetailsShowModal(false)
            setSearchResults([])
          }}
        />

        {/* Upgrade Modal */}
        <AppsTrackingPlanModal
          show={showUpgradeModal}
          handleClose={() => setShowUpgradeModal(false)}
          title=" Upgrade your plan to unlock this feature"
        />
        {showDeleteModal && gameToDelete && (
          <ConfirmRemoveTagModal
            show={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false)
              setGameToDelete(null)
            }}
            onConfirm={async ({ removeFromEverywhere }) => {
              setShowDeleteModal(false)
              if (!gameToDelete) return

              const isInGroup = activeGroup && activeGroup !== "all"

              if (isInGroup && !removeFromEverywhere) {
                await deleteGamefromTag(gameToDelete.item_id, activeGroup)
                triggerToast(`Removed from ${activeGroup}`)
              } else {
                await handleRemoveGame(gameToDelete)
                triggerToast("Removed from tracking")
                if (isInGroup) {
                  await getGameTags()
                }
              }
              setGameToDelete(null)
            }}
            itemName={gameToDelete.name}
            tagName={
              activeGroup && activeGroup !== "all" ? activeGroup : null
            }
          />
        )}

        <style>{`
    .go-to-publisher-btn {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      background: linear-gradient(90deg, #4b5563 0%, #374151 100%);
      border: 1px solid rgba(255,255,255,0.06);
      color: #fff;
      padding: 10px 18px;
      border-radius: 999px;
      font-weight: 700;
      font-size: 14px;
      cursor: pointer;
      box-shadow: 0 6px 16px rgba(0,0,0,0.35);
      transition: transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease;
      position: relative;
    }
    .go-to-publisher-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 12px 24px rgba(0,0,0,0.36), 0 6px 20px rgba(255,255,255,0.02);
    }
    .go-to-publisher-btn:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
    .go-to-publisher-btn .go-icon { display: inline-block; flex: 0 0 auto; }
    .go-to-publisher-btn .go-label { display: inline-block; }
    .go-to-publisher-btn .go-loading { display: inline-block; }
  `}</style>
      </div>
    </React.Fragment >
  )
}

export default CategoryTrackingLandingPage